import { Button, Empty, Tag } from "antd";
import { useNavigate } from "react-router";
import type { Schemas } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { useActiveWarehouse } from "@/lib/useWarehouses";

export type Location = Schemas["Location"];
export type Receipt = Schemas["Receipt"];
export type Task = Schemas["Task"];
export type StockCount = Schemas["StockCount"];
export type Pool = Schemas["Pool"];
export type Wave = Schemas["Wave"];

/**
 * The warehouse a warehouse screen works in: the active warehouse chosen in
 * the header (Plan §6.14). current is set only when it runs WMS.
 */
export function useWmsWarehouse() {
  const { current, loading } = useActiveWarehouse();
  const wms = current?.wmsEnabled ? current : undefined;
  return { current: wms, active: current, loading };
}

/** Shown when the active warehouse does not run WMS (or there is none). */
export function NoWms() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { current } = useActiveWarehouse();
  return (
    <Empty description={current ? t("wms.activeNoWms", { name: current.name }) : t("wms.noWmsWarehouse")} style={{ marginTop: 48 }}>
      {can("settings.warehouses") && (
        <Button type="primary" onClick={() => navigate("/app/settings/warehouses")}>
          {t("wms.openSettings")}
        </Button>
      )}
    </Empty>
  );
}

const kindColor: Record<string, string | undefined> = {
  receiving: "processing",
  returns: "cyan",
  packing: "purple",
  outbound: "blue",
  damaged: "error",
  quarantine: "warning",
  variance: "magenta",
};

export function LocationKindTag({ kind }: { kind: string }) {
  const { tDynamic } = useI18n();
  return <Tag color={kindColor[kind]}>{tDynamic(`wms.loc.kind.${kind}`, "wms.loc.kind.storage")}</Tag>;
}

const statusColor: Record<string, string | undefined> = { open: "default", receiving: "processing", in_progress: "processing", done: "success", cancelled: "default" };

export function ReceiptStatusTag({ status }: { status: Receipt["status"] }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`wms.in.status.${status}`)}</Tag>;
}

export function TaskStatusTag({ status }: { status: Task["status"] }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`wms.task.status.${status}`)}</Tag>;
}

export function CountStatusTag({ status }: { status: StockCount["status"] }) {
  const { t } = useI18n();
  return <Tag color={status === "open" ? "processing" : statusColor[status]}>{t(`wms.count.status.${status}`)}</Tag>;
}

const waveColor: Record<string, string> = { open: "processing", done: "success", cancelled: "default" };

export function WaveStatusTag({ status }: { status: Wave["status"] }) {
  const { t } = useI18n();
  return <Tag color={waveColor[status]}>{t(`wms.wave.status.${status}`)}</Tag>;
}

const methodColor: Record<string, string> = { single: "green", batch: "geekblue", cluster: "purple", auto: "default" };

/** Picking method: single-pcs, batch, cluster (or auto for a pool). */
/** The zone a picking task covers (zone picking). */
export function ZoneTag({ zone }: { zone: string }) {
  const { t } = useI18n();
  if (!zone) return null;
  return (
    <Tag color="geekblue" className="z-num" style={{ marginInlineEnd: 0 }}>
      {t("wms.zone.tag", { code: zone })}
    </Tag>
  );
}

export function MethodTag({ method }: { method: string }) {
  const { tDynamic } = useI18n();
  return <Tag color={methodColor[method]}>{tDynamic(`wms.method.${method}`, "wms.method.auto")}</Tag>;
}

const orderColor: Record<string, string> = { picking: "processing", picked: "cyan", packed: "blue", shipped: "success", removed: "default" };

export function WaveOrderTag({ status }: { status: string }) {
  const { tDynamic } = useI18n();
  return <Tag color={orderColor[status]}>{tDynamic(`wms.wave.order.${status}`, "wms.wave.order.picking")}</Tag>;
}
